import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {jwtDecode} from "jwt-decode";
import Select from "react-select-country-list";
import { PRICING } from "../../config/payment";
import "./RegisterPage.css";
import { clientId } from "../../config/config";

const RegisterPage = ({ setIsAuthenticated, setIsAdmin, setIsJudge }) => {
  const [activeTab, setActiveTab] = useState("login");

  // Login states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Registration states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [university, setUniversity] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [registerSuccess, setRegisterSuccess] = useState("");
  const [role, setRole] = useState("user");
  const [country, setCountry] = useState("");
  const [howHeard, setHowHeard] = useState("");
  const [job, setJob] = useState("Student");
  const [jobTitle, setJobTitle] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState("");
  const [teamMembers, setTeamMembers] = useState("");
  const [isRegisterDisabled, setIsRegisterDisabled] = useState(false);

  const navigate = useNavigate();
  const countryList = Select();
  const countryOptions = countryList.getData();

  const howHeardOptions = [
    { value: "Friend", label: "Friend" },
    { value: "Social Media", label: "Social Media" },
    { value: "Advertisement", label: "Advertisement" },
    { value: "Search Engine", label: "Search Engine" },
    { value: "Event", label: "Event" },
    { value: "Other", label: "Other" },
  ];

  const switchTab = (tab) => setActiveTab(tab);

  const calculateAmount = async (pricingTier, discountCode) => {
    if (!discountCode) return pricingTier;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/paypal/calculate-amount`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pricingTier, discountCode }),
      });
      const data = await res.json();
      if (data.success) return data.finalAmount;
      alert(data.error);
      return pricingTier;
    } catch (err) {
      console.error(err);
      return pricingTier;
    }
  };

  // Centralized validation
  const validateRegistration = () => {
    if (
      !firstName ||
      !lastName ||
      !registerEmail ||
      !registerPassword ||
      !confirmPassword ||
      !country ||
      !acceptPolicy ||
      !howHeard ||
      (job === "Student" && !university) ||
      (job === "Professional" && !jobTitle)
    ) {
      return false;
    }
    if (registerPassword !== confirmPassword) {
      alert("❌ Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      localStorage.setItem("token", response.data.token);
      const decoded = jwtDecode(response.data.token);
      localStorage.setItem("role", decoded.role);
      setLoginError("");
      setIsAuthenticated(true);
      setIsAdmin(decoded.role === "admin");
      setIsJudge(decoded.role === "judge");
      navigate(
        decoded.role === "admin"
          ? "/admin-dashboard"
          : decoded.role === "judge"
          ? "/judge-dashboard"
          : "/profile"
      );
    } catch (error) {
      setLoginError(error.response?.data?.message || "Error logging in");
      setLoginPassword("");
    }
  };

  const handleRegisterSubmit = async (e, paid = isPaid, amountPaid = 0, discountUsed = "") => {
    if (e) e.preventDefault();
    if (!validateRegistration()) return;

    if (!paid) {
      alert("⚠️ Please complete your payment first.");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        firstName,
        lastName,
        university,
        email: registerEmail,
        password: registerPassword,
        confirmPassword,
        role,
        country,
        howHeard,
        job,
        policyAccepted: acceptPolicy,
        jobTitle,
        teamMembers,
        amountPaid,
        discountUsed,
      });

      setIsRegisterDisabled(true);
      setRegisterSuccess("🎉 Registration successful! Please log in to continue.");
      setRegisterError("");

      setTimeout(() => {
        setRegisterSuccess("");
        setActiveTab("login");
        setIsRegisterDisabled(false);
      }, 3000);
    } catch (error) {
      setRegisterError(error.response?.data?.message || "Error during registration");
      setRegisterSuccess("");
    }
  };

  return (
    <div className="auth-container">
      <div className="tabs">
        <button className={`tab ${activeTab === "login" ? "active" : ""}`} onClick={() => switchTab("login")}>
          Log In
        </button>
        <button className={`tab ${activeTab === "register" ? "active" : ""}`} onClick={() => switchTab("register")}>
          Register
        </button>
      </div>

      {activeTab === "login" && (
        <form className="auth-form" onSubmit={handleLoginSubmit}>
          <h2>Log In</h2>
          {loginError && <div className="error-message">{loginError}</div>}
          <div>
            <label>Email</label>
            <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
          </div>
          <div>
            <label>Password</label>
            <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
          </div>
          <div className="forgot-password">
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
          <button className="auth-button" type="submit">Log In</button>
        </form>
      )}

      {activeTab === "register" && (
        <form className="auth-form" onSubmit={handleRegisterSubmit}>
          <h2>Register</h2>
          {registerError && <div className="error-message">{registerError}</div>}
          {registerSuccess && <div className="success-message">{registerSuccess}</div>}

          <div>
            <label>First Name</label>
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </div>
          <div>
            <label>Last Name</label>
            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </div>
          <div>
            <label>Are you a ...</label>
            <select value={job} onChange={(e) => setJob(e.target.value)} required>
              <option value="Student">Student</option>
              <option value="Professional">Professional</option>
            </select>
          </div>
          {job === "Student" && (
            <div>
              <label>University</label>
              <input type="text" value={university} onChange={(e) => setUniversity(e.target.value)} required />
            </div>
          )}
          {job === "Professional" && (
            <div>
              <label>Job Title</label>
              <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} required />
            </div>
          )}
          <div>
            <label>Team Members (optional)</label>
            <input
              type="text"
              value={teamMembers}
              onChange={(e) => setTeamMembers(e.target.value)}
              placeholder="Enter names separated by commas"
            />
          </div>
          <div>
            <label>Email</label>
            <input type="email" value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)} required />
          </div>
          <div>
            <label>Password</label>
            <input type="password" value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)} required />
          </div>
          <div>
            <label>Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
          </div>
          <div>
            <label>Country</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)} required>
              <option value="">Select Country</option>
              {countryOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label>How did you hear about us?</label>
            <select value={howHeard} onChange={(e) => setHowHeard(e.target.value)} required>
              <option value="">Select an Option</option>
              {howHeardOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Discount Code</label>
            <input type="text" value={appliedDiscountCode} onChange={(e) => setAppliedDiscountCode(e.target.value)} />
          </div>

          <div className="policy-check">
            <input type="checkbox" checked={acceptPolicy} onChange={() => setAcceptPolicy(!acceptPolicy)} required />
            <label>
              I accept the <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">terms and conditions</a>
            </label>
          </div>

          <PayPalScriptProvider options={{ "client-id": clientId }}>
            <div className="paypal-container">
              <PayPalButtons
                key={`${job}-${appliedDiscountCode}-${new Date().toDateString()}`}
                style={{ layout: "vertical" }}
                createOrder={async () => {
                  const pricingTier = PRICING[job] || 0;
                  const amount = await calculateAmount(pricingTier, appliedDiscountCode);

                  // Register immediately if discount makes it free
                  if (amount === 0) {
                    setIsPaid(true);
                    alert("✅ Discount applied! You don’t need to pay.");
                    await handleRegisterSubmit(null, true, 0, appliedDiscountCode || null);
                    return null;
                  }

                  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/paypal/direct-payment`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ amount, description: "Registration Fee" }),
                  });
                  const data = await response.json();
                  if (data.success) return data.orderId;
                  alert("❌ Payment creation failed: " + data.error);
                  return null;
                }}
                onApprove={async (data) => {
                  try {
                    const captureRes = await fetch(`${process.env.REACT_APP_API_URL}/api/paypal/capture-payment`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ orderId: data.orderID }),
                    });
                    const result = await captureRes.json();

if (result.success || result.status === "ONHOLD" || result.status === "PENDING") {
  setIsPaid(true);
                      alert("✅ Payment successful!");

                      if (appliedDiscountCode) {
                        await fetch(`${process.env.REACT_APP_API_URL}/api/discounts-mark-used?code=${appliedDiscountCode}`);
                      }

                      const pricingTier = PRICING[job] || 0;
                      const amount = await calculateAmount(pricingTier, appliedDiscountCode);

                      await handleRegisterSubmit(null, true, Number(amount), appliedDiscountCode || null);
                    } else {
                      alert("❌ Error capturing payment: " + result.error);
                    }
                  } catch (err) {
                    console.error(err);
                    alert("❌ Payment verification error.");
                  }
                }}
              />
            </div>
          </PayPalScriptProvider>

          <button className="auth-button" type="submit" disabled={isRegisterDisabled}>
            {isRegisterDisabled ? "Registered" : "Click Here After Payment"}
          </button>
        </form>
      )}
    </div>
  );
};

export default RegisterPage;
